import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonVariables = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Core Concepts</div>
                <h1>Variables & Memory Secrets</h1>
                <p className="hero-lead">
                    Forget everything you learned in C or Java. In Python, variables are not boxes that hold data;
                    they are sticky notes attached to objects floating in space.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    One of the most common hurdles for new Python developersespecially those coming from static languagesis
                    misunderstanding how variables work. If you think of a variable as a "container" that holds a value,
                    you will be constantly confused by how Python handles list modifications and function arguments.
                </p>
                <p>
                    <strong>The "Post-It Note" Analogy:</strong> In Python, everything is an <strong>Object</strong>. An integer is an object.
                    A string is an object. A function is an object. These objects float in a vast specific area of memory called the <strong>Heap</strong>.
                </p>
                <p>
                    A variable is simply a <strong>Name Tag</strong> or a <strong>Post-It Note</strong> that you stick onto one of these objects.
                    When you say <code>x = 10</code>, you aren't putting the number 10 inside a box labeled <code>x</code>. You are creating
                    an object <code>10</code> and sticking the note <code>x</code> onto it. If you then say <code>y = x</code>, you aren't copying the 10;
                    you are just sticking a second note labeled <code>y</code> onto the <em>same</em> object.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Reference Model:</strong> Why variables are just pointers.</li>
                        <li><strong>Identity:</strong> Using <code>id()</code> and <code>is</code> to see under the hood.</li>
                        <li><strong>Garbage Collection:</strong> How Python cleans up memory (The "Balloon String" theory).</li>
                        <li><strong>Mutability:</strong> Why some objects can change and others cannot.</li>
                        <li><strong>Common Pitfalls:</strong> The "Mutable Default Argument" trap.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Reference Model: Visualized</h2>
                <p>
                    Let's prove the "Post-It Note" theory using code. We can check the unique memory address of any object
                    using the built-in <code>id()</code> function.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>One Object, Two Names</span>
                    </div>
                    <CodeBlock language="python" code={`# We create a list object [1, 2, 3]
# We stick the label 'a' onto it.
a = [1, 2, 3]

# We stick the label 'b' onto the SAME object that 'a' is attached to.
b = a

# Let's prove they are the same object:
print(f"Address of a: {id(a)}")
print(f"Address of b: {id(b)}")
print(f"Are they the same? {a is b}")  # True

# Since they are the same object, modifying 'a' affects 'b'
a.append(4)
print(b)  # Output: [1, 2, 3, 4] -&gt; "Spooky action at a distance!"`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Mental Shift:</strong> In C++, <code>b = a</code> would typically copy the contents of <code>a</code> into a new memory location for <code>b</code>.
                        In Python, assignment <strong>NEVER copies data</strong>. It only copies the reference (the pointer).
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Memory Management: The "Balloon" Theory</h2>
                <p>
                    If variables are just labels, what happens when you remove the label? Or when you create an object but don't give it a name?
                    This brings us to Python's memory manager.
                </p>
                <h3>Reference Counting</h3>
                <p>
                    Imagine every object memory is a helium balloon. A variable (reference) is a string tying that balloon to the ground.
                    Python keeps a count of how many strings (references) are attached to each balloon.
                </p>
                <ul>
                    <li><code>x = [1, 2]</code> -&gt; The list `[1, 2]` has <strong>1</strong> reference (string).</li>
                    <li><code>y = x</code> -&gt; Now it has <strong>2</strong> references.</li>
                    <li><code>del x</code> -&gt; One string is cut. Count goes down to <strong>1</strong>. (The balloon is still safe).</li>
                    <li><code>y = None</code> -&gt; The last string is cut. Count goes to <strong>0</strong>.</li>
                </ul>
                <p>
                    <strong>The Reaper:</strong> When an object's reference count drops to zero, the balloon flies away (the memory is immediately freed).
                    This is Python's primary Garbage Collection mechanism.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Viewing Reference Counts</span>
                    </div>
                    <CodeBlock language="python" code={`import sys

# Create a unique string object
s = "Supercalifragilistic"

# sys.getrefcount returns the count. 
# Note: It is always 1 higher than you expect because 
# passing 's' to the function itself creates a temporary reference!
print(sys.getrefcount(s))  # e.g., 2

# Create another reference
b = s
print(sys.getrefcount(s))  # e.g., 3`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Identity vs Equality (`is` vs `==`)</h2>
                <p>
                    Because of this reference model, we have two ways to check if things are "the same".
                </p>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Operator</th>
                                <th>Name</th>
                                <th>Question Asked</th>
                                <th>Analogy</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>==</code></td>
                                <td>Equality</td>
                                <td>Do these two objects have the same <em>value</em>?</td>
                                <td>Do these two houses look identical?</td>
                            </tr>
                            <tr>
                                <td><code>is</code></td>
                                <td>Identity</td>
                                <td>Is this the <em>same object</em> in memory?</td>
                                <td>Is this the exact same house address?</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="item-grid-2col">
                    <div className="code-example">
                        <div className="code-header">
                            <span>Same Value, Different Object</span>
                        </div>
                        <CodeBlock language="python" code={`list1 = [1, 2, 3]
list2 = [1, 2, 3]

print(list1 == list2) # True (Values match)
print(list1 is list2) # False (Different boxes)`} />
                    </div>
                    <div className="code-example">
                        <div className="code-header">
                            <span>Same Object</span>
                        </div>
                        <CodeBlock language="python" code={`list1 = [1, 2, 3]
list2 = list1

print(list1 == list2) # True
print(list1 is list2) # True (Same box)`} />
                    </div>
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Singleton Rule:</strong> Always use <code>is</code> when comparing to <code>None</code>, <code>True</code>, or <code>False</code>.
                        These are "Singletons" (there is only one `None` object in the entire Python universe).
                        <br />
                        <code>if x is None:</code>
                        <br />
                         <code>if x == None:</code>
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Performance Hacking: Integer Caching</h2>
                <p>
                    You might notice something weird if you play with <code>id()</code> on small numbers.
                </p>
                <CodeBlock language="python" code={`a = 5
b = 5
print(a is b)  # True?! (Should be False for different variables, right?)

x = 5000
y = 5000
print(x is y)  # False (As expected)`} />
                <p>
                    <strong>What is happening?</strong> Python pre-allocates (caches) integers from <strong>-5 to 256</strong> when it starts up.
                    Because these numbers are used so frequently, Python decides it's inefficient to create a new object every time you calculate <code>1 + 1</code>.
                    Instead, it just points you to the existing <code>2</code> object.
                </p>
                <p>
                    This is an implementation detail of CPython (the standard Python). Do not rely on it for logic, but it's great trivia for interviews!
                </p>
            </div>

            <div className="content-section best-practices">
                <h2>The Mutable Default Argument Trap</h2>
                <p>
                    The most famous "Gotcha" in Python is directly related to variables being references.
                    If you use a mutable object (like a list) as a default argument, it is created <em>once</em> when the function is defined, not every time the function runs.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Trap</span>
                    </div>
                    <CodeBlock language="python" code={`def add_employee(emp, list=[]):
    list.append(emp)
    return list

print(add_employee("John"))  # ['John']
print(add_employee("Jane"))  # ['John', 'Jane']  <-- WAIT, WHAT?
# We expected a new list but got the old one!`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>The Fix:</strong> Use <code>None</code> as the default, and create the list inside the function.
                    </div></ProTip>

                <CodeBlock language="python" code={`def add_employee(emp, list=None):
    if list is None:
        list = []  # New list created every time
    list.append(emp)
    return list`} />
            </div>

            <div className="content-section key-takeaways">
                <h2> Key Takeaways</h2>
                <div className="takeaway-grid">
                    <div className="takeaway-item">
                        <h3>Labels, Not Boxes</h3>
                        <p>Variables are references (pointers) to objects. Assignment attaches a name tag; it does not copy data.</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>Identity Check</h3>
                        <p>Use <code>is</code> to check if two variables point to the same memory address. Use <code>==</code> to check if values are equal.</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>Garbage Collection</h3>
                        <p>Python counts references. When the count hits zero, the memory is freed. Cyclic references are handled by a separate GC.</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>Mutability Matters</h3>
                        <p>Be careful when passing lists or dicts to functions. They are passed by reference, so functions can change your original data!</p>
                    </div>
                </div>
            </div>

            <div className="external-resources">
                <h3> Further Reading</h3>
                <div className="resource-links">
                    <a href="https://realpython.com/python-memory-management/" target="_blank" rel="noopener noreferrer">
                        RealPython: Memory Management Deep Dive
                    </a>
                    <a href="https://docs.python.org/3/c-api/memory.html" target="_blank" rel="noopener noreferrer">
                        Python Docs: Memory API
                    </a>
                    <a href="https://nedbatchelder.com/text/names.html" target="_blank" rel="noopener noreferrer">
                        Ned Batchelder: Facts and Myths about Python Names
                    </a>
                </div>
            </div>
        </div>
    );
};

export default PythonVariables;


